import {
  useCallback,
  FC,
  HTMLAttributes,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { lineHeightPx } from '@atlas-frontend/design-tokens';
import { Tooltip } from './Tooltip';
import { Placement } from '@floating-ui/utils';

export interface TextEllipsisProps
  extends PropsWithChildren, HTMLAttributes<HTMLElement> {
  tooltip?: ReactNode;
  strong?: boolean;
  rows?: number;
  onIsClipped?: (isClipped: boolean) => unknown;
  renderTooltip?: boolean | 'always';
  placement?: Placement | 'auto';
}

export const TextEllipsis: FC<TextEllipsisProps> = (props) => {
  const {
    children,
    tooltip = children,
    strong,
    rows,
    onIsClipped,
    renderTooltip = true,
    placement = 'auto',
    ...rest
  } = props;
  // We need to track the element in state so that we can trigger a re-render when it changes,
  // which allows the tooltip to update its position if the content changes
  const [ellipsisElement, setEllipsisElement] = useState<HTMLElement | null>(
    null,
  );
  const ellipsisRef = useCallback((element: HTMLElement | null) => {
    setEllipsisElement(element);
  }, []);
  const { isClipped } = useDetectClipping(
    ellipsisElement,
    Boolean(tooltip),
    Boolean(rows && rows > 1),
    onIsClipped,
  );

  const ellipsis = (
    <Ellipsis
      as={strong ? 'strong' : 'span'}
      ref={ellipsisRef}
      $rows={rows}
      {...rest}
    >
      {children}
    </Ellipsis>
  );

  return renderTooltip ? (
    <Tooltip
      title={tooltip}
      placement={placement}
      disabled={!isClipped && renderTooltip !== 'always'}
    >
      {ellipsis}
    </Tooltip>
  ) : (
    ellipsis
  );
};

const Ellipsis = styled.span<{ $rows?: number }>`
  overflow: hidden;
  max-width: 100%;
  min-width: 0;

  ${({ $rows }) =>
    $rows && $rows > 1
      ? `
  display: -webkit-box;
  -webkit-line-clamp: ${$rows};
  -webkit-box-orient: vertical;
  min-height: ${lineHeightPx}px;`
      : `
  white-space: nowrap;
  text-overflow: ellipsis;
  display: inline-block;
  vertical-align: top;`}
`;

const useDetectClipping = (
  element: HTMLElement | null,
  isEnabled: boolean,
  isMultiRow: boolean,
  onClippingChange?: (isClipped: boolean) => unknown,
) => {
  const [isClipped, setIsClipped] = useState(false);
  const clippingRef = useRef(false);

  useEffect(() => {
    const updateState = (clipped: boolean) => {
      if (clippingRef.current === clipped) return;
      clippingRef.current = clipped;
      setIsClipped(clipped);
      onClippingChange?.(clipped);
    };

    // If the element is not available or clipping detection is disabled, ensure the state is reset
    if (!element || !isEnabled) {
      updateState(false);
      return;
    }

    const updateClipping = () => {
      const { scrollWidth, offsetWidth, scrollHeight, offsetHeight } = element;

      if (isMultiRow && offsetHeight) {
        updateState(scrollHeight > offsetHeight);
      }

      if (!isMultiRow && offsetWidth) {
        updateState(scrollWidth > offsetWidth);
      }
    };

    // Set up a ResizeObserver to detect changes in the element's size and update clipping accordingly
    // using requestAnimationFrame to batch updates
    let animationFrame: number | undefined;
    const resizeObserver = new ResizeObserver(() => {
      if (animationFrame === undefined) {
        animationFrame = requestAnimationFrame(() => {
          animationFrame = undefined;
          updateClipping();
        });
      }
    });

    updateClipping();
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
      if (animationFrame !== undefined) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [element, isEnabled, isMultiRow, onClippingChange]);

  return { isClipped };
};
